import { useEffect, useRef } from 'react';
import { X, ExternalLink, Clock, BookOpen, Tag } from 'lucide-react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { Post } from '../lib/api';

// Configure marked for clean output
marked.setOptions({ breaks: true, gfm: true } as Parameters<typeof marked.setOptions>[0]);

const LIVE_BASE: Record<string, string> = {
  blog:  'https://inboxme.space/blog',
  guide: 'https://inboxme.space/guides',
};

const LANG_PREFIX: Record<string, string> = {
  en: '', tr: '/tr', de: '/de', fr: '/fr', id: '/id',
  pl: '/pl', pt: '/pt', ar: '/ar', hi: '/hi', ur: '/ur',
};

const SITE_BASE  = 'https://inboxme.space';
const MEDIA_BASE = 'https://media.inboxme.space';

function renderBody(md: string): string {
  try {
    const raw = marked.parse(md) as string;
    const sanitized = DOMPurify.sanitize(raw, { USE_PROFILES: { html: true } });
    // Images are served from R2 (media.inboxme.space), links from the main site.
    // Both break in the admin panel on its own domain — prefix each accordingly.
    return sanitized.replace(
      /(src|href)="(\/[^"]+)"/g,
      (_, attr, path) => `${attr}="${attr === 'src' ? MEDIA_BASE : SITE_BASE}${path}"`
    );
  } catch {
    return `<p>${md.replace(/\n/g, '<br/>')}</p>`;
  }
}

interface Props {
  post: Post;
  onClose: () => void;
}

export default function PostPreviewPanel({ post, onClose }: Props) {
  const overlayRef = useRef<HTMLDivElement>(null);

  // Close on Escape
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [onClose]);

  const ptype    = post.post_type || 'blog';
  const langPfx  = LANG_PREFIX[post.lang] || '';
  const liveUrl  = `https://inboxme.space${langPfx}/${ptype === 'guide' ? 'guides' : 'blog'}/${post.slug}`;
  const r2Base   = 'https://pub-b434af2c8db241b99ec0a34311d30d51.r2.dev';
  const heroImg  = post.cover_image_url || `${r2Base}/blog/${post.slug}.jpg`;
  const rawTags  = Array.isArray(post.tags) ? post.tags.join(',') : (post.tags || '');
  const tags     = rawTags ? rawTags.split(',').map((t: string) => t.trim()).filter(Boolean) : [];

  const bodyHtml = renderBody(post.body || '');

  return (
    <>
      {/* Backdrop */}
      <div
        ref={overlayRef}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[300]"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="fixed inset-y-0 right-0 z-[301] w-full max-w-2xl flex flex-col bg-white dark:bg-gray-900 shadow-2xl">
        {/* ── Header ── */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700 bg-white/90 dark:bg-gray-900/90 backdrop-blur-md flex-shrink-0">
          <div className="flex items-center gap-2.5 min-w-0">
            <span className="text-xs font-bold uppercase tracking-widest text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/30 px-2 py-1 rounded-lg flex-shrink-0">
              Preview
            </span>
            <span className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">{post.title}</span>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {post.status === 'published' && (
              <a href={liveUrl} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white text-xs font-semibold rounded-xl hover:bg-emerald-700 transition">
                <ExternalLink size={12} /> View Live
              </a>
            )}
            <button onClick={onClose}
              className="p-2 rounded-xl text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition">
              <X size={18} />
            </button>
          </div>
        </div>

        {/* ── Scrollable content ── */}
        <div className="flex-1 overflow-y-auto">
          {/* Hero image */}
          {heroImg && (
            <div className="w-full h-52 sm:h-64 bg-gray-100 dark:bg-gray-800 overflow-hidden flex-shrink-0">
              <img
                src={heroImg}
                alt={post.title}
                className="w-full h-full object-cover"
                onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
            </div>
          )}

          <div className="px-6 py-6">
            {/* Type + status badges */}
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold ${
                ptype === 'guide'
                  ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300'
                  : 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
              }`}>
                {ptype === 'guide' ? <BookOpen size={10} /> : null}
                {ptype === 'blog' ? '📝' : '📖'} {ptype.charAt(0).toUpperCase() + ptype.slice(1)}
              </span>
              <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${
                post.status === 'published' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' :
                post.status === 'draft'     ? 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300' :
                post.status === 'scheduled' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                                              'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
              }`}>{post.status}</span>
              {post.read_time_min && (
                <span className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                  <Clock size={11} /> {post.read_time_min} min read
                </span>
              )}
            </div>

            {/* Title */}
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-50 leading-tight mb-3">
              {post.title}
            </h1>

            {/* Meta description */}
            {post.meta_description && (
              <p className="text-sm text-gray-500 dark:text-gray-400 italic border-l-4 border-purple-300 dark:border-purple-700 pl-3 mb-4">
                {post.meta_description}
              </p>
            )}

            {/* Tags */}
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-5">
                {tags.map((t: string, i: number) => (
                  <span key={i} className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-gray-100 dark:bg-gray-700/60 text-gray-600 dark:text-gray-300 text-xs rounded-full">
                    <Tag size={9} /> {t}
                  </span>
                ))}
              </div>
            )}

            <div className="border-t border-gray-200 dark:border-gray-700 mb-5" />

            {/* Rendered body */}
            <div
              className="prose prose-sm sm:prose dark:prose-invert max-w-none
                prose-headings:font-bold prose-headings:text-gray-900 dark:prose-headings:text-gray-100
                prose-h2:text-xl prose-h2:mt-6 prose-h2:mb-2
                prose-h3:text-base prose-h3:mt-4 prose-h3:mb-1
                prose-p:text-gray-700 dark:prose-p:text-gray-300 prose-p:leading-relaxed
                prose-strong:text-gray-900 dark:prose-strong:text-gray-100
                prose-a:text-purple-600 dark:prose-a:text-purple-400 prose-a:no-underline hover:prose-a:underline
                prose-ul:list-disc prose-ol:list-decimal prose-li:text-gray-700 dark:prose-li:text-gray-300
                prose-img:rounded-xl prose-img:my-4 prose-img:w-full prose-img:object-cover
                prose-blockquote:border-l-purple-400 prose-blockquote:text-gray-500
                prose-code:bg-gray-100 dark:prose-code:bg-gray-800 prose-code:rounded prose-code:px-1"
              dangerouslySetInnerHTML={{ __html: bodyHtml }}
            />

            {/* CTA section */}
            {post.cta_text && (
              <div className="mt-8 p-5 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 border border-purple-200 dark:border-purple-800/50 rounded-2xl">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-3">{post.cta_text}</p>
                {post.cta_button_label && (
                  <span className="inline-block px-4 py-2 bg-purple-600 text-white text-sm font-semibold rounded-xl">
                    {post.cta_button_label}
                  </span>
                )}
              </div>
            )}

            {/* Live URL hint */}
            <div className="mt-6 p-3 bg-gray-50 dark:bg-gray-800/60 rounded-xl">
              <p className="text-[11px] text-gray-400 font-mono break-all">{liveUrl}</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
